import * as Constants from '../../../constants';
import { Cross } from '../../atoms';
import { FileInProgressModal } from '../../molecules';
import { FileUpload } from '@u1/aligntech-uikit';
import { bulkDeleteCanaryUsersRequest } from '../../../clients/canaryUsers';
import { useGlobalContext } from '../../context';
import ChosenFileModal from '../../molecules/ChosenFileModal/ChosenFileModal';
import FileDeleteResultModal from '../../molecules/FileResultModal/FileDeleteResultModal';
import FileErrorModal from '../../molecules/FileErrorModal/FileErrorModal';
import React from 'react';
import useStyles from './UsersFileDelete.styles';

interface IUsersFileDeleteProps {
  open?: boolean;
  uploadBtnText?: string;
  acceptMultiple?: boolean;
  closeFileIsDeletedModal: () => void;
  handleGeneralError: (text: string, title: string) => void;
}

interface IUsersBulkDeleteModal {
  isShowFileDeleteResultsModal: boolean;
  deletedUsers?: number;
  notFoundUsers?: number;
  usersNotFound?: string[];
  isShowSuccessMessageOnly: boolean;
}

export const UsersFileDelete: React.FC<IUsersFileDeleteProps> = ({
  open = false,
  uploadBtnText,
  acceptMultiple = false,
  closeFileIsDeletedModal,
  handleGeneralError,
}) => {
  const classes = useStyles();
  const { appConfig } = useGlobalContext();
  const [isShowModalDeleteFile, setIsShowModalDeleteFile] =
    React.useState<boolean>();
  const [isShowModalFileDeleteInProgress, setShowModalFileDeleteInProgress] =
    React.useState<boolean>();
  const [isShowFileDeleteMainModal, setShowFileDeleteMainModal] =
    React.useState<boolean>();
  const [fileDeleteResultsModal, setFileDeleteResultsModal] =
    React.useState<IUsersBulkDeleteModal>({
      isShowFileDeleteResultsModal: false,
      deletedUsers: 0,
      notFoundUsers: 0,
      usersNotFound: [] as string[],
      isShowSuccessMessageOnly: false,
    });
  const [fileToDelete, setFileToDelete] = React.useState<File | null>();

  const [fileDeleteErrorModal, setFileDeleteErrorModal] = React.useState({
    isShowFileDeleteErrorModal: false,
    title: '',
  });

  const setFileDeleteModalErrorText = (title = '') => {
    if (title) {
      setFileDeleteErrorModal({
        isShowFileDeleteErrorModal: true,
        title: title,
      });
    } else {
      setFileDeleteErrorModal({
        isShowFileDeleteErrorModal: false,
        title: title,
      });
    }
  };

  const onFileAddedHandler = (fileList: FileList) => {
    setFileToDelete(fileList.item(0));
    setIsShowModalDeleteFile(true);
  };

  const handleFileDeleteErrorModal = () => {
    setFileDeleteModalErrorText();
    setShowFileDeleteMainModal(false);
  };

  const handleFileDeleteResultIsShownModal = () => {
    setFileDeleteResultsModal({
      isShowFileDeleteResultsModal: false,
      deletedUsers: 0,
      notFoundUsers: 0,
      usersNotFound: [],
      isShowSuccessMessageOnly: false,
    });
    closeFileIsDeletedModal();
  };

  const onFileDeleteHandler = () => {
    setIsShowModalDeleteFile(false);
    setShowFileDeleteMainModal(false);
    setShowModalFileDeleteInProgress(true);
    return bulkDeleteCanaryUsersRequest(appConfig, fileToDelete)
      .then(response => {
        setShowModalFileDeleteInProgress(false);
        setFileDeleteResultsModal({
          isShowFileDeleteResultsModal: true,
          deletedUsers: response.deleted,
          notFoundUsers: response.notFound,
          usersNotFound: response.usersNotFound,
          isShowSuccessMessageOnly: !response,
        });
      })
      .catch(async reason => {
        setShowModalFileDeleteInProgress(false);
        if (reason.toString().includes('SyntaxError')) {
          setFileDeleteResultsModal({
            isShowFileDeleteResultsModal: true,
            isShowSuccessMessageOnly: true,
          });
        } else {
          if (reason.status && reason.status === 409) {
            const jsonReason = await reason.text();
            setFileDeleteModalErrorText(JSON.parse(jsonReason).message);
          } else {
            handleGeneralError(
              Constants.GENERAL_ERROR_MESSAGE,
              Constants.GENERAL_ERROR_TITLE,
            );
          }
        }
      });
  };

  const onFileDeleteCloseHandler = () => {
    closeFileIsDeletedModal();
  };

  const onDeleteChosenFileModalCloseHandler = () => {
    setIsShowModalDeleteFile(false);
    closeFileIsDeletedModal();
  };

  return (
    <>
      {open && (
        <div className={classes.root}>
          {!isShowFileDeleteMainModal && (
            <>
              <div className={classes.closeButton}>
                <Cross onClick={onFileDeleteCloseHandler} />
              </div>
              <div className={classes.fileUpload} data-qa-name="input_file">
                <FileUpload
                  onUpload={onFileAddedHandler}
                  uploadBtnText={uploadBtnText}
                  acceptMultiple={acceptMultiple}
                />
              </div>
            </>
          )}

          {/* ------------------------- */}
          {/* CONFIRM FILE DELETE MODAL */}
          {/* ------------------------- */}
          <ChosenFileModal
            open={isShowModalDeleteFile}
            text={fileToDelete?.name}
            buttonText="Upload"
            buttonSize="small"
            handleButtonClick={onFileDeleteHandler}
            handleClose={onDeleteChosenFileModalCloseHandler}
            iconType="delete"
          />

          {/* ----------------------------- */}
          {/* FILE DELETE IN PROGRESS MODAL */}
          {/* ----------------------------- */}
          <FileInProgressModal open={isShowModalFileDeleteInProgress} />

          {/* ----------------------- */}
          {/* FILE DELETE ERROR MODAL */}
          {/* ----------------------- */}
          <FileErrorModal
            open={fileDeleteErrorModal.isShowFileDeleteErrorModal}
            headerText={fileDeleteErrorModal.title}
            text={Constants.CHOOSE_ANOTHER_FILE_MESSAGE}
            buttonText="Ok"
            buttonSize="small"
            handleButtonClick={handleFileDeleteErrorModal}
          />

          {/* ------------------------ */}
          {/* FILE DELETE RESULT MODAL */}
          {/* ------------------------ */}
          <FileDeleteResultModal
            open={fileDeleteResultsModal.isShowFileDeleteResultsModal}
            headerText={Constants.DELETE_FROM_FILE_TITLE}
            text={Constants.ADD_A_FILE_SUCCESS_MESSAGE}
            deletedUsers={fileDeleteResultsModal.deletedUsers}
            notFoundUsers={fileDeleteResultsModal.notFoundUsers}
            usersNotFoundList={fileDeleteResultsModal.usersNotFound}
            downloadMessageText={Constants.DOWNLOAD_NOT_FOUND_USERS_MESSAGE}
            showSuccessMessageOnly={
              fileDeleteResultsModal.isShowSuccessMessageOnly
            }
            buttonText="Ok"
            buttonSize="small"
            handleButtonClick={handleFileDeleteResultIsShownModal}
          />
        </div>
      )}
    </>
  );
};
